import { Injectable, NgZone } from '@angular/core';
import { Environment } from '@libraries/shared-env';
import {
  initialize,
  LDClient,
  LDFlagChangeset,
  LDFlagSet,
} from 'launchdarkly-js-client-sdk';
import { Observable, of, Subject } from 'rxjs';

@Injectable()
export class LaunchDarklyService {
  private client?: LDClient;
  private initSubject: Subject<LDFlagSet> = new Subject<LDFlagSet>();
  private liveSubject: Subject<LDFlagSet> = new Subject<LDFlagSet>();
  private flags: LDFlagSet;

  constructor(private env: Environment, private zone: NgZone) {
    this.flags = env['featureFlagConfig'] || ({} as LDFlagSet);
    if (this.hasClientId()) {
      this.client = this._initialize();
      this.client.on('ready', this._ready.bind(this));
      this.client.on('change', this._change.bind(this));
    }
  }

  hasClientId(): boolean {
    return this.env.launchDarklyClientId ? true : false;
  }

  /**
   * Gets flags or our initSubject if the client isn't ready
   *
   * @returns {Observable<LDFlagSet>}
   * @memberof LaunchDarklyService
   */
  getFlags(): Observable<LDFlagSet> {
    if (!this.initSubject.closed) {
      return this.initSubject.asObservable();
    }
    return of(this.flags);
  }

  /**
   * Gets live flag changes that can be observed
   *
   * @returns {Observable<LDFlagSet>}
   * @memberof LaunchDarklyService
   */
  getFlagsLive(): Observable<LDFlagSet> {
    return this.liveSubject.asObservable();
  }

  /**
   * Initializes LaunchDarkly client
   *
   * @returns {LDClient}
   * @memberof LaunchDarklyService
   */
  _initialize(): LDClient {
    return this.zone.runOutsideAngular(() =>
      initialize(this.env.launchDarklyClientId, {
        anonymous: true,
        kind: 'user'
      })
    );
  }

  /**
   * LaunchDarkly client 'ready' event callback
   * Provides initialized flags
   *
   * @private
   * @memberof LaunchDarklyService
   */
  private _ready() {
    const clientFlags = Object.keys(this.flags).reduce(
      (accu: LDFlagSet, flag) => {
        accu[flag] = this.client?.variation(flag, this.flags[flag]);
        return accu;
      },
      {}
    );
    this.flags = { ...this.flags, ...clientFlags };
    this.initSubject.next(this.flags);
    this.initSubject.complete();
    this._change();
  }

  /**
   * LaunchDarkly client 'change' event callback
   * Provides live flag changes
   *
   * @private
   * @param {LDFlagChangeset} [changes={}]
   * @memberof LaunchDarklyService
   */
  private _change(changes: LDFlagChangeset = {}) {
    const changed = Object.keys(changes)
      .filter((key) => changes[key].current != null)
      .reduce((flags: LDFlagSet, key) => {
        flags[key] = changes[key].current;
        return flags;
      }, {});
    this.flags = { ...this.flags, ...changed };
    this.liveSubject.next(this.flags);
  }
}
